import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import HorizontalScroll from '../../Molecules/HorizontalScroll/HorizontalScroll';
import { Image } from '../../Atoms';

/**
 * CarouselImage - Autoplaying image carousel with optional heading/subtitle overlay
 *
 * Data shape (after camelCase conversion):
 * value = [
 *   {
 *     image: { original, srcset, srcsetWebp, ... },
 *     heading: 'optional',
 *     subtitle: 'optional',
 *   }
 * ]
 *
 * Section-level props:
 * heading  — optional section heading above carousel
 * subtitle — optional section subtitle above carousel
 * interval — autoplay interval in ms (default 5000)
 */
const CarouselImage = ({
  value = [],
  heading,
  subtitle,
  interval = 5000,
}) => {
  // Ref on the outer media-element div — exists on first render, no chicken-and-egg
  const mediaRef = useRef(null);
  const [itemWidth, setItemWidth] = useState(300); // safe fallback until measured

  // Measure the outer container — this is what each slide should match
  useEffect(() => {
    if (!mediaRef.current) return;

    const measure = () => {
      if (mediaRef.current) {
        setItemWidth(mediaRef.current.offsetWidth);
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(mediaRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay — advances one slide every `interval` ms, loops back to start
  useEffect(() => {
    if (!value || value.length <= 1) return;

    const timer = setInterval(() => {
      const container = mediaRef.current?.querySelector('.horizontal-scroll');
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;
      const atEnd = scrollLeft >= scrollWidth - clientWidth - 10;

      container.scrollTo({
        left: atEnd ? 0 : scrollLeft + itemWidth,
        behavior: 'smooth',
      });
    }, interval);

    return () => clearInterval(timer);
  }, [value, interval, itemWidth]);

  if (!value || value.length === 0) return null;

  return (
    <section className="section section-full">
      <div className="section__inner">
        <div className="viewport-90">

          {/* Section header — only renders if heading or subtitle provided */}
          {(heading || subtitle) && (
            <div className="content-element content-element-tight">
              <div className="stack stack-tight">
                {heading && <div className="text-section-title">{heading}</div>}
                {subtitle && <p className="text-section-support">{subtitle}</p>}
              </div>
            </div>
          )}

          {/* Carousel — ref is on the outer div, always present on first render */}
          <div className="media-element image-carousel-media" ref={mediaRef}>
            <HorizontalScroll
              items={value}
              itemWidth={itemWidth}
              gap="tight"
              showArrows={true}
            >
              {(slide) => (
                <div
                  className={[
                    'media-element',
                    slide.heading || slide.subtitle ? 'media-element--overlay' : '',
                    slide.heading || slide.subtitle ? 'media-element--overlay-medium' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <Image image={slide.image} fit="cover" />
                  {(slide.heading || slide.subtitle) && (
                    <>
                      <div className="media-element__gradient" />
                      <div className="media-element__content">
                        <div className="media-element__inner">
                          <div className="stack stack-tight">
                            {slide.heading && (
                              <div className="text-item-title">{slide.heading}</div>
                            )}
                            {slide.subtitle && (
                              <div className="text-label">{slide.subtitle}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </HorizontalScroll>
          </div>

        </div>
      </div>
    </section>
  );
};

CarouselImage.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.object.isRequired,
      heading: PropTypes.string,
      subtitle: PropTypes.string,
    })
  ),
  heading: PropTypes.string,
  subtitle: PropTypes.string,
  interval: PropTypes.number,
};

export default CarouselImage;